import React, {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import i18next from 'i18next';
import styles from './styles.module.scss';
import {Route} from '~services/RoutesService';
import MapToggle from '~components/MapToggle';
import {Map} from './components/Map';
import MapSidePanel from './components/MapSidePanel';
import ActionCard from './components/ActionCard';
import TerritoryCard from './components/TerritoryCard';
import CardSkeleton from './components/CardSkeleton';
import BaseMapSelector from '~components/BaseMapSelector';
import {BaseMap, readBaseMap, saveBaseMap} from '~components/BaseMapSelector/baseMap';
import {Project} from "~screens/Dashboard/screens/Projects/types";
import {useParams} from "react-router";

// Territories carry an Acción count; Acciones carry their territories.
const isAction = (element: Route | Project): element is Project => (element as Project).territories !== undefined;
// Placeholder rows while loading; fewer when the territory's count is known to be smaller.
const SKELETON_ROWS = 6;

const ElementsMap = ({listPath, listTitle, dataLoader, routeDataLoader, selectable}: {
    listPath: (string | ((params: any) => Promise<string>)),
    listTitle?: (string | ((params: any) => Promise<string>)),
    dataLoader: (params, location) => Promise<(Route | Project)[]>,
    routeDataLoader?: (params, location) => Promise<Route | undefined>,
    selectable?: boolean
}) => {
    const params = useParams();
    const location = useLocation();
    // A territory card hands over the territory it opens: its name, count and outline draw before its Acciones load.
    const handedTerritory = (location.state as { territory?: Route } | null)?.territory;
    const seed = handedTerritory && String(handedTerritory.id) === params.id ? handedTerritory : undefined;

    const [state, setState] = useState<{
        elements: (Route | Project)[],
        hoveredId?: number,
        listPath: string,
        listTitle: string,
        route?: Route,
        loaded: boolean
    }>(() => ({
        elements: [],
        hoveredId: undefined,
        listPath: '',
        listTitle: seed?.name ?? (typeof listTitle === 'string' ? listTitle : ''),
        route: seed,
        loaded: false
    }));

    useEffect(() => {
        let cancelled = false;
        setState(current => ({...current, loaded: false, ...(seed ? {listTitle: seed.name, route: seed} : {})}));

        const dataPromise = dataLoader(params, location);
        const listPathPromise = typeof listPath !== 'string' ? listPath(params) : new Promise<string>((resolve) => {
            resolve(listPath)
        });
        const listTitlePromise = listTitle === undefined ? '' : (typeof listTitle !== 'string' ? listTitle(params) : new Promise<string>((resolve) => {
            resolve(listTitle)
        }));
        const routeDataPromise = routeDataLoader ? routeDataLoader(params, location) : new Promise<Route | undefined>((resolve) => {
            resolve(undefined);
        });

        // Await all four promises and then update state; a response for a screen already left is dropped.
        Promise.all([dataPromise, listPathPromise, listTitlePromise, routeDataPromise]).then(value => {
            const [elements, listPath, listTitle, route] = value;
            if (!cancelled) {
                setState(current => ({...current, elements, listPath, listTitle, route, loaded: true}));
            }
        });
        return () => {
            cancelled = true;
        };
        // The loaders only read the territory id and the list's query.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id, location.search]);


    const onHighlight = (id?: number) => setState(current => ({...current, hoveredId: id}));

    const [baseMap, setBaseMap] = useState<BaseMap>(readBaseMap);
    const changeBaseMap = (next: BaseMap) => {
        setBaseMap(next);
        saveBaseMap(next);
    };

    const {elements, hoveredId} = state;

    const {listPath: resolvedListPath, listTitle: resolvedListTitle, route, loaded} = state;
    // A territory's map: its name under a breadcrumb, and how many Acciones it has.
    const isTerritory = Boolean(params.id);
    const breadcrumb = isTerritory && resolvedListTitle ? [
        {name: i18next.t('map:home') as string, url: '/home'},
        {name: i18next.t('map:maps') as string, url: '/territories/map'},
        {name: resolvedListTitle}
    ] : undefined;
    const title = resolvedListTitle || (isTerritory ? '' : i18next.t('map:byTerritory') as string);
    const count = isTerritory ? (loaded ? elements.length : seed?.actions) : undefined;
    const skeletonRows = Math.min(count ?? SKELETON_ROWS, SKELETON_ROWS);

    return (
        <div className={styles.container}>
            <MapSidePanel title={title} breadcrumb={breadcrumb} count={count} busy={!loaded}>
                {!loaded && Array.from({length: skeletonRows}, (_, index) => (
                    <CardSkeleton key={index} territory={listPath === '/territories'}/>
                ))}
                {loaded && elements.map(element => isAction(element)
                    ? <ActionCard key={element.id} action={element} onHighlight={onHighlight}
                                  territoryName={isTerritory ? resolvedListTitle : undefined}/>
                    : <TerritoryCard key={element.id} territory={element} onHighlight={onHighlight}/>)}
            </MapSidePanel>
            <div className={styles.mapColumn}>
                <div className={styles.controls}>
                    <MapToggle listPath={resolvedListPath} />
                    <BaseMapSelector value={baseMap} onChange={changeBaseMap} />
                </div>
                <Map elements={elements} hoveredId={hoveredId} route={route} selectable={selectable} baseMap={baseMap}/>
            </div>
        </div>
    );
}

export default ElementsMap;
